import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { ACTIONS, EVENTS, Joyride, STATUS } from "react-joyride";

const getStorageKey = (workspaceId) => `mz_product_tour_v1:${workspaceId || "global"}`;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const slugify = (value) =>
  String(value || "project")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export default function ProductTour() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const canViewProjectDetail = auth.workspace_role === "ADMIN" || auth.workspace_role === "MANAGER";
  const location = useLocation();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [firstProjectPath, setFirstProjectPath] = useState("");
  const [hasProjects, setHasProjects] = useState(false);
  const [shouldAutoStart, setShouldAutoStart] = useState(false);

  const storageKey = getStorageKey(auth.workspace_id);

  useEffect(() => {
    const dismissedValue = localStorage.getItem(storageKey) === "dismissed";
    setDismissed(dismissedValue);
  }, [storageKey]);

  useEffect(() => {
    const handleStartTour = () => {
      if (dismissed) return;
      setShouldAutoStart(true);
    };

    window.addEventListener("minutezilla:start-product-tour", handleStartTour);
    return () => window.removeEventListener("minutezilla:start-product-tour", handleStartTour);
  }, [dismissed]);

  useEffect(() => {
    const loadTourContext = async () => {
      if (!auth.token || !auth.workspace_id || dismissed) {
        setLoaded(true);
        return;
      }

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        };
        const body = JSON.stringify({
          workspace_id: auth.workspace_id,
          mode: 0,
          clients: "",
          projectTerm: "",
        });
        const res = await axios.post("/api/project/list", body, config);
        const projects = res.data.results || [];
        const firstProject = projects.find((item) => item && item.id && item.project_name);

        setHasProjects(projects.length > 0);
        setFirstProjectPath(
          canViewProjectDetail && firstProject
            ? `/project/${firstProject.id}/${slugify(firstProject.project_name)}`
            : ""
        );
      } catch (err) {
        console.log(err);
        setHasProjects(false);
        setFirstProjectPath("");
      } finally {
        setLoaded(true);
      }
    };

    loadTourContext();
  }, [auth.token, auth.workspace_id, dismissed, canViewProjectDetail]);

  useEffect(() => {
    if (!loaded || dismissed || !hasProjects || !firstProjectPath) return;
    if (location.pathname === "/dashboard" || location.pathname === "/projects" || shouldAutoStart) {
      const t = setTimeout(() => setRun(true), 1200);
      return () => clearTimeout(t);
    }
  }, [loaded, dismissed, hasProjects, firstProjectPath, location.pathname, shouldAutoStart]);

  const finishTour = () => {
    localStorage.setItem(storageKey, "dismissed");
    setDismissed(true);
    setRun(false);
    setStepIndex(0);
    setShouldAutoStart(false);
  };

  const goToRoute = async (path) => {
    if (!path || location.pathname === path) return;
    navigate(path, { replace: true });
    await wait(250);
  };

  const openProjectPicker = async () => {
    const toggle = document.querySelector('[data-tour="dashboard-project-picker"]');
    if (toggle && !document.querySelector('[data-tour="dashboard-project-search"]')) {
      toggle.click();
      await wait(200);
    }
  };

  const openProjectEditModal = async () => {
    const editButton = document.querySelector('[data-tour="project-edit-button"]');
    if (editButton) {
      editButton.click();
      await wait(250);
    }
  };

  const steps = !hasProjects || !firstProjectPath ? [] : [
    {
      id: "dashboard-project-picker",
      target: '[data-tour="dashboard-project-picker"]',
      placement: "bottom",
      get content() { return tr("Pick the project you want to track here. We use this selector every time you start a timer."); },
      before: async () => {
        await goToRoute("/dashboard");
      },
    },
    {
      id: "dashboard-create-timer",
      target: '[data-tour="dashboard-create-timer"]',
      placement: "left",
      get content() { return tr("Start or stop the timer from this button. It is the quickest way to log work from the dashboard."); },
      before: async () => {
        await goToRoute("/dashboard");
      },
    },
    {
      id: "dashboard-project-search",
      target: '[data-tour="dashboard-project-search"]',
      placement: "bottom",
      get content() { return tr("Use search when you have a long project list. This keeps the picker fast even with many projects."); },
      before: async () => {
        await goToRoute("/dashboard");
        await openProjectPicker();
      },
    },
    {
      id: "sidebar-workspace-switch",
      target: '[data-tour="sidebar-workspace-switcher"]',
      placement: "right",
      get content() { return tr("Switch workspaces from here. The dashboard and project picker will update to the selected workspace."); },
      before: async () => {
        await goToRoute("/dashboard");
      },
    },
    {
      id: "projects-search",
      target: '[data-tour="projects-search"]',
      placement: "bottom",
      get content() { return tr("The Projects page lets you search by name, filter by client, and review the full project list."); },
      before: async () => {
        await goToRoute("/projects");
      },
    },
    {
      id: "project-edit-button",
      target: '[data-tour="project-edit-button"]',
      placement: "bottom",
      get content() { return tr("This opens the project editor where you can update the name, client, color, and summary."); },
      before: async () => {
        await goToRoute(firstProjectPath);
      },
    },
    {
      id: "project-save-button",
      target: '[data-tour="project-save-button"]',
      placement: "top",
      get content() { return tr("Save changes here. The tour opens the editor so you can see the full edit-and-save flow end to end."); },
      before: async () => {
        await goToRoute(firstProjectPath);
        await openProjectEditModal();
      },
    },
  ];

  const handleEvent = (data) => {
    const { action, index, status, type } = data;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      finishTour();
    }
  };

  if (!loaded || dismissed || !hasProjects || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      run={run}
      stepIndex={stepIndex}
      steps={steps}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      styles={{
        options: {
          arrowColor: "#ffffff",
          backgroundColor: "#ffffff",
          primaryColor: "#1a5c30",
          textColor: "#111827",
          overlayColor: "rgba(17, 24, 39, 0.42)",
          zIndex: 5000,
        },
      }}
      locale={{
        back: tr("Back"),
        close: tr("Close"),
        last: tr("Done"),
        next: tr("Next"),
        nextWithProgress: tr("Next ({current}/{total})"),
        skip: tr("Skip tour"),
      }}
      options={{
        showProgress: true,
        skipBeacon: true,
        targetWaitTimeout: 5000,
        beforeTimeout: 5000,
      }}
    />
  );
}
